/**
 * Whether a `RichTextEditor` value holds no readable content.
 *
 * tiptap serialises an empty document as `<p></p>`, not as an empty string, so
 * a body that looks blank in the editor reaches callers in one of several
 * shapes depending on what the user did:
 *
 * - `''` — the editor was never focused (the `createForm()` initial value)
 * - `'<p></p>'` — typed into, then cleared
 * - `'<p><br></p>'` — cleared with a trailing break left behind
 *
 * All of them are empty here. Without this, the first shape and the second
 * behave differently on submit: one fails the backend's `.nonempty()` check
 * (a 500 plus a Sentry event and a Slack alert), the other passes it and
 * stores a body that renders as nothing.
 *
 * Emptiness is decided on **text** content, matching the check this replaces
 * (`text.replace(/<[^>]*>/g, '').trim()`), which the report description
 * completeness indicators have used since the body became rich text. One
 * consequence is worth knowing: markup carrying no text — an empty table, say —
 * also counts as empty. That is what callers want (a body with nothing
 * readable in it) but it is not the same as "the document has no nodes", which
 * is how `RichTextEditor` decides its own internal `isEmpty`.
 */
export function isRichTextEmpty(html: string | null | undefined): boolean {
  if (!html) return true

  // `DOMParser` is a browser API. Every caller today is a component, and the tests run
  // in jsdom — but this sits in `globals/utils`, where the next caller could be a node
  // script, and there it would throw rather than answer. Falling back keeps the helper
  // usable outside the browser; the fallback is the check this function replaced, so the
  // degradation is to the previous behaviour rather than to something untested. It
  // differs on one case: a literal `&nbsp;` entity reads as non-empty, since only the
  // parser decodes it. Flagged by Copilot on the MIL-291 PR.
  if (typeof DOMParser === 'undefined') {
    return !html
      .replace(/<[^>]*>/g, '')
      .replace(/\u00a0/g, ' ')
      .trim()
  }

  // parseFromString rather than assigning innerHTML: it is inert, and it
  // decodes entities, so a paragraph holding only `&nbsp;` reads as empty
  // (`trim()` strips the decoded U+00A0, but not the literal entity text).
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return !doc.body.textContent?.trim()
}
